import { useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useLocation } from '@tanstack/react-router'
import { type UseFormReturn } from 'react-hook-form'

import { getWalletInfoQueryKey, type GetWalletInfoResponse } from '@/shared/api'
import { useExperiment, useExperimentsReady } from '@/shared/hooks/use-experiment'

import { getStoredPaymentMethod } from '@/features/payment-method'

import {
    buildFinanceTopupDoneClickEvent,
    buildFinanceTopupDoneRedirectEvent,
    buildFinanceTopupMethodChangedClickEvent,
    buildFinanceTopupSumClickEvent,
    buildFinanceTopupSumErrorEvent,
    buildFinanceTopupTagSumClickEvent,
    buildFinanceTopupViewEvent,
} from './build-metrics'
import { useDriverRechargeMetricsStore } from './metrics-store'
import type { TFormValues } from './types'
import type { DriverRechargeMetricsInput } from './types-metrics'

type Props = {
    formMethods: UseFormReturn<TFormValues>
}

// finance_topup_sum_click
const financeTopupSumClickEvent = (clickFrom: 'button' | 'placeholder') => {
    return buildFinanceTopupSumClickEvent(clickFrom)
}

// finance_topup_sum_error
const financeTopupSumErrorEvent = (error: string) => {
    return buildFinanceTopupSumErrorEvent(error)
}

// finance_topup_method_changed_click
const financeTopupMethodChangedClickEvent = (paymentMethod: string) => {
    return buildFinanceTopupMethodChangedClickEvent(paymentMethod)
}

export const useMetrics = ({ formMethods }: Props) => {
    const location = useLocation()
    const source = location.search.source
    const queryClient = useQueryClient()
    const walletInfo = queryClient.getQueryData<GetWalletInfoResponse>(getWalletInfoQueryKey())
    const growthExperiment = useExperiment('driver_recharge_growth')
    const experimentsReady = useExperimentsReady()
    const selectedTagAmount = useDriverRechargeMetricsStore((state) => state.selectedTagAmount)
    const setSelectedTagAmount = useDriverRechargeMetricsStore(
        (state) => state.setSelectedTagAmount,
    )

    const input: DriverRechargeMetricsInput = useMemo(() => {
        // Булево значение баланса
        const balanceValue = walletInfo ? walletInfo.balance.amount > 0 : false
        // Метод пополнения по умолчанию
        const paymentMethodDefault = getStoredPaymentMethod().id
        // Массив сумм пополнения
        const tagsSum = walletInfo?.topup.presets.map((preset) => preset / 100) ?? []

        return {
            paymentMethodDefault,
            balanceValue,
            tagsSum,
            experimentsReady,
            growthExperiment,
        }
    }, [walletInfo, growthExperiment, experimentsReady])

    // finance_topup_view
    const financeTopupViewEvent = useMemo(
        () => buildFinanceTopupViewEvent(input, Number(formMethods.watch('value')) / 100, source),
        [input, formMethods, source],
    )

    // finance_topup_tag_sum_click
    const financeTopupTagSumClickEvent = (selectedAmount: number) => {
        setSelectedTagAmount(selectedAmount)

        return buildFinanceTopupTagSumClickEvent(
            formMethods.watch('paymentMethod').id,
            input,
            selectedAmount,
            Number(formMethods.watch('value')) / 100,
        )
    }

    // finance_topup_done_click
    const financeTopupDoneClickEvent = (error?: string) => {
        return buildFinanceTopupDoneClickEvent(
            input,
            formMethods.watch('paymentMethod').id,
            Number(formMethods.watch('value')) / 100,
            selectedTagAmount,
            error ?? undefined,
        )
    }

    // finance_topup_done_redirect
    const financeTopupDoneRedirectEvent = (paymentId: string) => {
        return buildFinanceTopupDoneRedirectEvent(
            input,
            formMethods.watch('paymentMethod').id,
            Number(formMethods.watch('value')) / 100,
            selectedTagAmount,
            paymentId,
        )
    }

    return {
        financeTopupViewEvent,
        financeTopupSumClickEvent,
        financeTopupSumErrorEvent,
        financeTopupTagSumClickEvent,
        financeTopupMethodChangedClickEvent,
        financeTopupDoneClickEvent,
        financeTopupDoneRedirectEvent,
    }
}
